import React, { useEffect, useState, useCallback } from "react";
import BusinessApiService from "../../services/BusinessApiService";
import VariantsTab from "./VariantsTab";
import { getCurrentBusinessId } from "../../services/api";
import {
  Plus, Search, Edit, Trash2, Package, BarChart3,
  AlertTriangle, RefreshCw, X, Globe, ChevronDown,
  ChevronUp, ShoppingCart, Tag,
} from "lucide-react";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "";

const CATEGORIES = [
  'Medicine','Vitamins','Fashion','Beauty','Food & Beverage',
  'Footwear','Luxury Watches','Luxury Bags','Skincare','Alcohol',
  'Electronics','Other',
];

// ✅ FIX: Expanded from 5 to 19 currencies matching Product.js enum
const CURRENCIES = [
  'USD','EUR','GBP','CAD','AUD','VND',
  'SGD','JPY','THB','MYR','IDR','PHP',
  'KRW','AED','SAR','MXN','BRL','NZD','HKD',
];

const CURRENCY_LABELS: Record<string, string> = {
  USD:'US Dollar',EUR:'Euro',GBP:'British Pound',CAD:'Canadian Dollar',
  AUD:'Australian Dollar',VND:'Vietnamese Dong',SGD:'Singapore Dollar',
  JPY:'Japanese Yen',THB:'Thai Baht',MYR:'Malaysian Ringgit',
  IDR:'Indonesian Rupiah',PHP:'Philippine Peso',KRW:'Korean Won',
  AED:'UAE Dirham',SAR:'Saudi Riyal',MXN:'Mexican Peso',
  BRL:'Brazilian Real',NZD:'New Zealand Dollar',HKD:'Hong Kong Dollar',
};

const MARKETS = [
  { code:'VN', flag:'🇻🇳', label:'Vietnam' },
  { code:'US', flag:'🇺🇸', label:'United States' },
  { code:'SG', flag:'🇸🇬', label:'Singapore' },
  { code:'JP', flag:'🇯🇵', label:'Japan' },
  { code:'AU', flag:'🇦🇺', label:'Australia' },
  { code:'GB', flag:'🇬🇧', label:'United Kingdom' },
  { code:'CA', flag:'🇨🇦', label:'Canada' },
  { code:'DE', flag:'🇩🇪', label:'Germany' },
  { code:'FR', flag:'🇫🇷', label:'France' },
  { code:'KR', flag:'🇰🇷', label:'South Korea' },
  { code:'TH', flag:'🇹🇭', label:'Thailand' },
  { code:'MY', flag:'🇲🇾', label:'Malaysia' },
  { code:'ID', flag:'🇮🇩', label:'Indonesia' },
  { code:'PH', flag:'🇵🇭', label:'Philippines' },
  { code:'AE', flag:'🇦🇪', label:'UAE' },
  { code:'SA', flag:'🇸🇦', label:'Saudi Arabia' },
  { code:'CN', flag:'🇨🇳', label:'China' },
  { code:'NZ', flag:'🇳🇿', label:'New Zealand' },
  { code:'MX', flag:'🇲🇽', label:'Mexico' },
  { code:'BR', flag:'🇧🇷', label:'Brazil' },
  { code:'GL', flag:'GL', label:'Global' },

];

const LANGUAGES = [
  { code:'en', label:'English' },
  { code:'vi', label:'Vietnamese (Tiếng Việt)' },
  { code:'ja', label:'Japanese (日本語)' },
  { code:'ko', label:'Korean (한국어)' },
  { code:'zh', label:'Chinese (中文)' },
  { code:'th', label:'Thai (ภาษาไทย)' },
  { code:'id', label:'Indonesian (Bahasa)' },
  { code:'fr', label:'French (Français)' },
  { code:'de', label:'German (Deutsch)' },
];

const PAYMENT_GATEWAYS = [
  { code:'stripe', label:'Stripe (international)', flag:'💳' },
  { code:'vnpay',  label:'VNPay (Vietnam)',         flag:'🇻🇳' },
  { code:'momo',   label:'MoMo (Vietnam)',           flag:'🟣' },
  { code:'paypay', label:'PayPay (Japan)',            flag:'🇯🇵' },
  { code:'paypal', label:'PayPal',                   flag:'🔵' },
  { code:'manual', label:'Manual / Offline',         flag:'📋' },
];

const DEFAULT_GATEWAY: Record<string,string> = {
  VN:'vnpay', JP:'paypay', US:'stripe', AU:'stripe',
  GB:'stripe', CA:'stripe', SG:'stripe', DE:'stripe', FR:'stripe',
};

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface ProductImage {
  url: string;
  alt?: string;
  isPrimary?: boolean;
  order?: number;
}

interface Product {
  productId: string;
  _id?: string;
  businessId: string;
  name: string;
  description?: string;
  category: string;
  brand?: string;
  model?: string;
  sku: string;
  status: 'draft' | 'active' | 'inactive' | 'archived';
  pricing?: {
    retail: number;
    wholesale?: number;
    currency: string;
    salePrice?: number;
    onSale?: boolean;
  };
  inventory?: {
    currentStock: number;
    minStock: number;
    reservedStock?: number;
  };
  shipping?: { weight?: number; weightUnit?: string; freeShipping?: boolean; };
  images?: ProductImage[];
  urls?: { website?: string; };
  showOnShop?: boolean;
  marketPricing?: Array<{
    country: string; price: number; currency: string;
    shippingCost?: number; freeShipping?: boolean;
    minDays?: number; maxDays?: number;
    salePrice?: number; onSale?: boolean; isActive?: boolean;
    shipFromCountry?: string; shippingLabel?: string; notes?: string;
  }>;
  targetMarkets?: string[];
  shopStats?: { totalViews?: number; totalOrders?: number; totalRevenue?: number; };
  gs1?: { gtin?: string; isOwnGtin?: boolean; status?: string; };
  createdAt?: string;
}

interface ShopListing {
  listingId:       string;
  masterProductId: string;
  country:         string;
  language:        string;
  paymentGateway:  string;
  name:            string;
  description?:    string;
  price:           number;
  currency:        string;
  salePrice?:      number;
  onSale?:         boolean;
  shipping?: {
    cost?: number; free?: boolean;
    shipFromCountry?: string;
    minDays?: number; maxDays?: number; label?: string;
  };
  status:    string;
  isActive:  boolean;
  isFeatured?: boolean;
  shopStats?: { totalViews?: number; totalOrders?: number; };
}

interface ProductFormData {
  name: string; description: string; category: string;
  brand: string; model: string; sku: string;
  status: 'draft' | 'active' | 'inactive' | 'archived';
  retailPrice: number; wholesalePrice: number; currency: string;
  stock: number; lowStockThreshold: number; reserved: number;
  website: string; salePrice: number; onSale: boolean;
  showOnShop: boolean; freeShipping: boolean;
  shippingWeight: number; shippingWeightUnit: string;
  gtin: string;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getBackendRoot = () =>
  (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "")
    .replace(/\/api\/?$/, "");

const fixImageUrl = (url: string): string => {
  if (!url) return "";
  const root = getBackendRoot();
  if (url.includes("localhost") || url.includes("jeptags.com/uploads")) {
    const path = url.replace(/^https?:\/\/[^/]+/, "");
    return `${root}${path}`;
  }
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `${root}${url}`;
  return `${root}/uploads/images/products/${url}`;
};

const getProductImages = (product: Product): ProductImage[] =>
  (product.images || []).map(img => ({ ...img, url: fixImageUrl(img.url) }));

const getStatusColor = (status?: string) => {
  switch (status) {
    case "active":   return "bg-green-100 text-green-800";
    case "draft":    return "bg-gray-100 text-gray-800";
    case "inactive": return "bg-yellow-100 text-yellow-800";
    case "archived": return "bg-red-100 text-red-800";
    default:         return "bg-gray-100 text-gray-800";
  }
};

const marketMeta = (code: string) =>
  MARKETS.find(m => m.code === code) || { code, flag: '🌐', label: code };

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const ProductsPage = () => {
  const [products, setProducts]           = useState<Product[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [searchTerm, setSearchTerm]       = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus]     = useState("all");
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showEditModal, setShowEditModal]       = useState(false);
  const [editingProduct, setEditingProduct]     = useState<Product | null>(null);
  const [editTab, setEditTab]             = useState<'basic'|'markets'|'variants'>('basic');
  const [refreshKey, setRefreshKey]       = useState(0);
  const [deleting, setDeleting]           = useState<string | null>(null);
  const [selectedImages, setSelectedImages]     = useState<File[]>([]);
  const [imagePreviews, setImagePreviews]       = useState<string[]>([]);
  const [uploadingImages, setUploadingImages]   = useState(false);
  const [editExistingImages, setEditExistingImages] = useState<ProductImage[]>([]);
  const [editNewImages, setEditNewImages]       = useState<File[]>([]);
  const [editNewPreviews, setEditNewPreviews]   = useState<string[]>([]);

  const [formData, setFormData] = useState<ProductFormData>({
    name:"", description:"", category:"", brand:"", model:"", sku:"",
    status:'draft', retailPrice:0, wholesalePrice:0, currency:"USD",
    stock:0, lowStockThreshold:10, reserved:0, website:"",
    salePrice:0, onSale:false, showOnShop:true, freeShipping:false,
    shippingWeight:0, shippingWeightUnit:"g",
    gtin:"",
  });

  // ── GS1 Barcode Lookup State ─────────────────────────────────────────────
  const [gtinInput, setGtinInput]     = useState("");
  const [gtinLooking, setGtinLooking] = useState(false);
  const [gtinResult, setGtinResult]   = useState<{source: string} | null>(null);
  const [gtinError, setGtinError]     = useState<string | null>(null);

  const lookupBarcode = async () => {
    const gtin = gtinInput.replace(/\D/g, '');
    if (gtin.length < 8) { setGtinError('Enter at least 8 digits'); return; }
    setGtinLooking(true); setGtinError(null); setGtinResult(null);
    try {
      const res = await fetch(`${API_BASE}/epcis/lookup?gtin=${gtin}`, {
        headers: { 'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}` }
      });
      const data = await res.json();
      if (data.success && data.autoFill) {
        const f = data.autoFill;
        const resolvedName = f.name || f.description || "";
        setFormData(p => ({
          ...p,
          name:        resolvedName || p.name,
          brand:       f.brand      || p.brand,
          category:    f.category   || p.category,
          description: f.description || p.description,
          gtin,
        }));
        setGtinResult({ source: data.source });
      } else {
        setGtinError('Product not found — enter details manually');
        setFormData(p => ({ ...p, gtin }));
      }
    } catch { setGtinError('Lookup failed — check connection'); }
    setGtinLooking(false);
  };

  const resetForm = () => {
    setFormData({
      name:"", description:"", category:"", brand:"", model:"", sku:"",
      status:'draft', retailPrice:0, wholesalePrice:0, currency:"USD",
      stock:0, lowStockThreshold:10, reserved:0, website:"",
      salePrice:0, onSale:false, showOnShop:true, freeShipping:false,
      shippingWeight:0, shippingWeightUnit:"g",
      gtin:"",
    });
    setSelectedImages([]); setImagePreviews([]);
    setGtinInput(""); setGtinResult(null); setGtinError(null);
  };

  // ── Load products ────────────────────────────────────────────────────────
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const businessId = getCurrentBusinessId();
      if (!businessId) throw new Error('No business ID. Please log in again.');
      // ✅ FIX: consistent API_BASE usage
    //  const res = await fetch(`${API_BASE}/products?businessId=${businessId}`, {
        // AFTER:
        const res = await fetch(`${API_BASE}/products?businessId=${businessId}&limit=1000`, {
        headers: {
          'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}`,
          'Content-Type': 'application/json',
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.data?.products || data.products || [];
      setProducts(list.map((p: any) => ({
        ...p,
        productId: p.productId || p._id,
        images: p.images || [],
      })));
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProducts(); }, [refreshKey, loadProducts]);
  useEffect(() => () => { imagePreviews.forEach(URL.revokeObjectURL); }, [showCreateModal]);

  // ── Create product ───────────────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.category || !formData.brand.trim() || !formData.sku.trim()) {
      alert('Please fill in all required fields'); return;
    }
    if (!formData.description.trim() || formData.description.trim().length < 10) {
      alert('Description must be at least 10 characters'); return;
    }
    try {
      setUploadingImages(true);
      let uploadedImages: ProductImage[] = [];
      if (selectedImages.length > 0) {
        // ✅ FIX: consistent API_BASE usage
        const imageForm = new FormData();
        selectedImages.forEach(img => imageForm.append("images", img));
        // NOTE: Images are uploaded to /api/upload/images during create (product ID not yet known).
        // After create, the returned productId can be used with /api/product/:id/images for per-product management.
        const imgRes = await fetch(`${API_BASE}/upload/images`, { method:"POST", body:imageForm,
          headers: { 'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}` } });
        const imgResult = await imgRes.json();
        if (imgResult.success && Array.isArray(imgResult.images)) {
          uploadedImages = imgResult.images.map((img: any, idx: number) => ({
            url: img.original?.url || img.url || "",
            alt: `${formData.name} image ${idx + 1}`,
            isPrimary: idx === 0, order: idx,
          }));
        } else {
          alert("Failed to upload images: " + imgResult.error);
          setUploadingImages(false); return;
        }
      }
      setUploadingImages(false);
      const businessId = getCurrentBusinessId();
      if (!businessId) throw new Error("No business ID found.");

      const res = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name, description: formData.description,
          category: formData.category, brand: formData.brand,
          model: formData.model, sku: formData.sku, status: formData.status,
          businessId,
          pricing: {
            retail: formData.retailPrice,
            wholesale: formData.wholesalePrice > 0 ? formData.wholesalePrice : undefined,
            currency: formData.currency,
            salePrice: formData.salePrice > 0 ? formData.salePrice : undefined,
            onSale: formData.onSale,
          },
          inventory: {
            currentStock: formData.stock, minStock: formData.lowStockThreshold,
            maxStock: 1000, reservedStock: formData.reserved, trackInventory: true,
          },
          shipping: {
            weight: formData.shippingWeight > 0 ? formData.shippingWeight : undefined,
            weightUnit: formData.shippingWeightUnit, freeShipping: formData.freeShipping,
          },
          showOnShop: formData.showOnShop, isActive: true, images: uploadedImages,
          urls: { website: formData.website || undefined },
        }),
      });

      const result = await res.json();
      if (res.ok && result.success) {
        alert(`Product "${formData.name}" created!`);
        resetForm(); setShowCreateModal(false); setRefreshKey(k => k + 1);
      } else {
        alert(`Failed to create: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setUploadingImages(false);
      alert(`Failed to create product: ${err.message}`);
    }
  };

  // ── Delete product ───────────────────────────────────────────────────────
  const handleDelete = async (product: Product) => {
    if (!window.confirm(`Delete "${product.name}"? Cannot be undone.`)) return;
    const pid = product.productId || product._id;
    if (!pid) { alert("Product ID not found."); return; }
    try {
      setDeleting(pid);
      const res = await fetch(`${API_BASE}/products/${pid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}` },
      });
      if (res.ok) {
        setRefreshKey(k => k + 1);
      } else {
        const r = await res.json().catch(() => ({}));
        alert(`Failed to delete: ${r.error || r.message || `HTTP ${res.status}`}`);
      }
    } catch (err: any) {
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  // ── Open edit modal ──────────────────────────────────────────────────────
  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setEditTab('basic');
    setFormData({
      name: product.name || "", description: product.description || "",
      category: product.category || "", brand: product.brand || "",
      model: product.model || "", sku: product.sku || "",
      status: product.status || 'draft',
      retailPrice: product.pricing?.retail || 0,
      wholesalePrice: product.pricing?.wholesale || 0,
      currency: product.pricing?.currency || "USD",
      stock: product.inventory?.currentStock || 0,
      lowStockThreshold: product.inventory?.minStock || 10,
      reserved: product.inventory?.reservedStock || 0,
      website: product.urls?.website || "",
      salePrice: product.pricing?.salePrice || 0,
      onSale: product.pricing?.onSale || false,
      showOnShop: product.showOnShop !== false,
      freeShipping: product.shipping?.freeShipping || false,
      shippingWeight: product.shipping?.weight || 0,
      shippingWeightUnit: product.shipping?.weightUnit || "g",
      gtin: (product as any).gs1?.gtin || "",
    });
    setEditExistingImages(getProductImages(product));
    setEditNewImages([]); setEditNewPreviews([]);
    setShowEditModal(true);
  };

  const handleRemoveExistingImage = (idx: number) =>
    setEditExistingImages(prev => prev.filter((_, i) => i !== idx));

  const handleEditImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEditNewImages(prev => [...prev, ...files]);
    setEditNewPreviews(prev => [...prev, ...files.map(URL.createObjectURL)]);
  };

  const handleRemoveEditNewImage = (idx: number) => {
    setEditNewPreviews(prev => { URL.revokeObjectURL(prev[idx]); return prev.filter((_,i)=>i!==idx); });
    setEditNewImages(prev => prev.filter((_,i)=>i!==idx));
  };

  // ── Update product ───────────────────────────────────────────────────────
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;
    const pid = editingProduct.productId || editingProduct._id;
    if (!pid) { alert("Product ID not found."); return; }
    if (!formData.name.trim() || !formData.category || !formData.brand.trim() || !formData.sku.trim()) {
      alert('Please fill in all required fields'); return;
    }
    try {
      setUploadingImages(true);
      const businessId = getCurrentBusinessId();
      let newUploaded: ProductImage[] = [];
      if (editNewImages.length > 0) {
        const imageForm = new FormData();
        editNewImages.forEach(img => imageForm.append("images", img));
        // Use per-product image endpoint for updates (product ID is known)
        const imgRes = await fetch(`${API_BASE}/upload/images`, { method:"POST", body:imageForm,
          headers: { 'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}` } });
        const imgResult = await imgRes.json();
        if (imgResult.success && Array.isArray(imgResult.images)) {
          newUploaded = imgResult.images.map((img: any, idx: number) => ({
            url: img.original?.url || img.url || "",
            alt: `${formData.name} image ${editExistingImages.length + idx + 1}`,
            isPrimary: false, order: editExistingImages.length + idx,
          }));
        } else {
          alert("Failed to upload images: " + (imgResult.error || "Unknown error"));
          setUploadingImages(false); return;
        }
      }
      setUploadingImages(false);
      const allImages = [
        ...editExistingImages.map((img, idx) => ({ ...img, order: idx, isPrimary: idx === 0 })),
        ...newUploaded,
      ];

      const res = await fetch(`${API_BASE}/products/${pid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${BusinessApiService.getBusinessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name, description: formData.description,
          category: formData.category, brand: formData.brand,
          model: formData.model, sku: formData.sku, status: formData.status,
          businessId,
          pricing: {
            retail: formData.retailPrice,
            wholesale: formData.wholesalePrice > 0 ? formData.wholesalePrice : undefined,
            currency: formData.currency,
            salePrice: formData.salePrice > 0 ? formData.salePrice : undefined,
            onSale: formData.onSale,
          },
          inventory: {
            currentStock: formData.stock, minStock: formData.lowStockThreshold,
            reservedStock: formData.reserved, trackInventory: true,
          },
          shipping: {
            weight: formData.shippingWeight > 0 ? formData.shippingWeight : undefined,
            weightUnit: formData.shippingWeightUnit, freeShipping: formData.freeShipping,
          },
          showOnShop: formData.showOnShop, images: allImages,
          urls: { website: formData.website || undefined },
        }),
      });

      if (res.ok) {
        resetForm();
        setEditExistingImages([]); setEditNewImages([]);
        setEditNewPreviews(prev => { prev.forEach(URL.revokeObjectURL); return []; });
        setShowEditModal(false); setEditingProduct(null);
        setRefreshKey(k => k + 1);
      } else {
        const r = await res.json().catch(() => ({}));
        alert(`Failed to update: ${r.error || r.message || `HTTP ${res.status}`}`);
      }
    } catch (err: any) {
      alert(`Failed to update: ${err.message}`);
    } finally {
      setUploadingImages(false); // always reset spinner
    }
  };

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = {
    total:      products.length,
    active:     products.filter(p => p.status === 'active').length,
    lowStock:   products.filter(p => { const s = p.inventory?.currentStock||0; return s>0 && s<=(p.inventory?.minStock||10); }).length,
    outOfStock: products.filter(p => (p.inventory?.currentStock||0) === 0).length,
    onShop:     products.filter(p => p.showOnShop !== false && p.status === 'active').length,
    countries: products.filter(p => (p.marketPricing||[]).length > 0).length,
  };

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = products.filter(p => {
    const q = searchTerm.toLowerCase();
    return (
      (!q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) ||
       p.brand?.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)) &&
      (selectedCategory === 'all' || p.category === selectedCategory) &&
      (selectedStatus === 'all' || p.status === selectedStatus)
    );
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-2 text-gray-600">Loading products...</p>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Product Catalog</h2>
          <p className="text-gray-600">Manage your products and multi-market pricing</p>
          {error && <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">⚠️ {error}</div>}
        </div>
        <div className="flex space-x-3">
          <button onClick={() => setRefreshKey(k=>k+1)}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center space-x-2">
            <RefreshCw size={16} /><span>Refresh</span>
          </button>
          <button onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2">
            <Plus size={20} /><span>Add Product</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label:'Total',       value:stats.total,       color:'blue',   icon:<Package size={24} /> },
          { label:'Active',      value:stats.active,      color:'green',  icon:<BarChart3 size={24} /> },
          { label:'Low Stock',   value:stats.lowStock,    color:'yellow', icon:<AlertTriangle size={24} /> },
          { label:'Out of Stock',value:stats.outOfStock,  color:'red',    icon:<AlertTriangle size={24} /> },
          { label:'On Shop',     value:stats.onShop,      color:'purple', icon:<ShoppingCart size={24} /> },
          { label:'Countries',   value:stats.countries,   color:'indigo', icon:<Globe size={24} /> },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</div>
                <div className="text-sm text-gray-600">{s.label}</div>
              </div>
              <div className={`text-${s.color}-400`}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Search products..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
          </select>
          <div className="text-sm text-gray-500 flex items-center">
            {filtered.length} of {products.length} products
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(product => (
            <div key={product.productId} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 text-center">
                {getProductImages(product).length > 0 ? (
                  <div className="flex flex-row gap-2 justify-center items-center mb-2">
                    {getProductImages(product).slice(0,3).map((img,idx) => (
                      <img key={img.url+idx} src={img.url} alt={img.alt||product.name}
                        className={`rounded-lg ${idx===0?'w-32 h-32':'w-20 h-20'} object-cover border ${img.isPrimary?'ring-2 ring-blue-400':''}`} />
                    ))}
                  </div>
                ) : <div className="text-4xl mb-4">📦</div>}
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(product.status)}`}>
                    {product.status}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{product.name}</h3>
                  <div className="text-right flex-shrink-0 ml-2">
                    {product.pricing?.onSale && product.pricing?.salePrice ? (
                      <>
                        <span className="text-sm text-gray-400 line-through">{product.pricing.retail} {product.pricing.currency}</span>
                        <span className="text-lg font-bold text-red-600 ml-1">{product.pricing.salePrice} {product.pricing.currency}</span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-blue-600">
                        {product.pricing?.retail?.toLocaleString() || 0} {product.pricing?.currency || 'USD'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {product.pricing?.onSale && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      {product.pricing.salePrice && product.pricing.retail
                        ? `${Math.round((1-product.pricing.salePrice/product.pricing.retail)*100)}% OFF`
                        : 'SALE'}
                    </span>
                  )}
                  {product.showOnShop !== false && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Shop</span>}
                  {product.shipping?.freeShipping && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Free Ship</span>}
                </div>
                {product.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                )}
                <div className="space-y-1.5 mb-4 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">SKU:</span><span className="font-mono text-xs">{product.sku}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Brand:</span><span className="font-medium">{product.brand||'N/A'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Category:</span><span className="font-medium">{product.category}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Stock:</span><span className="font-medium">{product.inventory?.currentStock||0} units</span></div>
                </div>
                <div className="space-y-2">
                  <button onClick={() => handleEditClick(product)}
                    className="w-full bg-yellow-50 text-yellow-600 px-3 py-2 rounded-lg hover:bg-yellow-100 text-sm flex items-center justify-center gap-2">
                    <Edit size={14} /><span>Edit</span>
                  </button>
                  <button onClick={() => handleDelete(product)}
                    disabled={deleting === (product.productId||product._id)}
                    className="w-full bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                    <Trash2 size={14} />
                    <span>{deleting===(product.productId||product._id)?'Deleting...':'Delete'}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your search or filters.'
              : 'Get started by adding your first product.'}
          </p>
          <button onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
            <Plus size={20} /><span>Add First Product</span>
          </button>
        </div>
      )}

      {/* ── CREATE MODAL ─────────────────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Add New Product</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-6">

              {/* ── GS1 Barcode Lookup ─────────────────────────────── */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-blue-900 mb-1">🏷️ Have an existing barcode?</h4>
                <p className="text-xs text-blue-600 mb-3">Scan or enter the product GS1 barcode to auto-fill details</p>
                <div className="flex gap-2">
                  <input type="text" value={gtinInput} onChange={e => setGtinInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), lookupBarcode())}
                    placeholder="Enter GTIN / UPC / EAN barcode..."
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" maxLength={14} />
                  <button type="button" onClick={lookupBarcode} disabled={gtinLooking}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {gtinLooking ? '🔍 Looking...' : '🔍 Lookup'}
                  </button>
                </div>
                {gtinResult && <p className="text-xs text-green-700 mt-2">✅ Found via {gtinResult.source} — form auto-filled!</p>}
                {gtinError && <p className="text-xs text-red-600 mt-2">⚠️ {gtinError}</p>}
                {formData.gtin && <p className="text-xs text-blue-700 mt-1 font-mono">GTIN: {formData.gtin}</p>}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                    <input type="text" value={formData.name} onChange={e=>setFormData(p=>({...p,name:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Brand *</label>
                    <input type="text" value={formData.brand} onChange={e=>setFormData(p=>({...p,brand:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <input type="text" value={formData.model} onChange={e=>setFormData(p=>({...p,model:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                    <input type="text" value={formData.sku} onChange={e=>setFormData(p=>({...p,sku:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <select value={formData.category} onChange={e=>setFormData(p=>({...p,category:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required>
                      <option value="">Select category...</option>
                      {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select value={formData.status} onChange={e=>setFormData(p=>({...p,status:e.target.value as any}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="draft">Draft</option><option value="active">Active</option><option value="inactive">Inactive</option>
                    </select></div>
                </div>
                <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                  <textarea value={formData.description} onChange={e=>setFormData(p=>({...p,description:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" rows={3} required /></div>
                <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-2">🔗 Product URL (optional)</label>
                  <input type="url" value={formData.website} onChange={e=>setFormData(p=>({...p,website:e.target.value}))} placeholder="https://yoursite.com/product-page" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-1">Internal Reference Pricing</h4>
                <p className="text-xs text-gray-400 mb-4">⚠️ These prices are <b>not shown to buyers</b>. Buyers see prices from Shop Listings only.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Cost / MSRP <span className="text-xs text-gray-400 font-normal">(internal only)</span></label>
                    <input type="number" step="0.01" value={formData.retailPrice} onChange={e=>setFormData(p=>({...p,retailPrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Wholesale / Buy Price <span className="text-xs text-gray-400 font-normal">(internal only)</span></label>
                    <input type="number" step="0.01" value={formData.wholesalePrice} onChange={e=>setFormData(p=>({...p,wholesalePrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select value={formData.currency} onChange={e=>setFormData(p=>({...p,currency:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      {CURRENCIES.map(c=><option key={c} value={c}>{c} — {CURRENCY_LABELS[c]}</option>)}
                    </select></div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.onSale} onChange={e=>setFormData(p=>({...p,onSale:e.target.checked}))} className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-red-600">On Sale</span>
                  </label>
                  {formData.onSale && (
                    <div className="mt-3 grid grid-cols-2 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Sale Price</label>
                        <input type="number" step="0.01" value={formData.salePrice} onChange={e=>setFormData(p=>({...p,salePrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-red-50" /></div>
                      {formData.salePrice > 0 && formData.retailPrice > 0 && (
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-red-600">{Math.round((1-formData.salePrice/formData.retailPrice)*100)}% OFF</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Inventory</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity</label>
                    <input type="number" value={formData.stock} onChange={e=>setFormData(p=>({...p,stock:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Low Stock Threshold</label>
                    <input type="number" value={formData.lowStockThreshold} onChange={e=>setFormData(p=>({...p,lowStockThreshold:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Reserved Stock</label>
                    <input type="number" value={formData.reserved} onChange={e=>setFormData(p=>({...p,reserved:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-lg font-medium text-blue-900 mb-4">🛒 Shop Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.showOnShop} onChange={e=>setFormData(p=>({...p,showOnShop:e.target.checked}))} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Sell on Shop</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.freeShipping} onChange={e=>setFormData(p=>({...p,freeShipping:e.target.checked}))} className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">Free Shipping (global)</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Shipping Weight</label>
                    <input type="number" step="0.01" value={formData.shippingWeight} onChange={e=>setFormData(p=>({...p,shippingWeight:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Weight Unit</label>
                    <select value={formData.shippingWeightUnit} onChange={e=>setFormData(p=>({...p,shippingWeightUnit:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option value="g">Grams (g)</option><option value="kg">Kilograms (kg)</option>
                      <option value="oz">Ounces (oz)</option><option value="lb">Pounds (lb)</option>
                    </select></div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Product Images</h4>
                <input type="file" multiple accept="image/*" onChange={e=>{const f=Array.from(e.target.files||[]);setSelectedImages(f);setImagePreviews(f.map(URL.createObjectURL));}}
                  className="block w-full border border-gray-300 rounded-lg p-2" />
                <div className="flex flex-wrap gap-4 mt-4">
                  {imagePreviews.map((url,idx)=>(
                    <div key={url} className="relative w-24 h-24">
                      <img src={url} alt={`Preview ${idx+1}`} className="w-24 h-24 object-cover rounded-lg border" />
                      <button type="button" onClick={()=>{setSelectedImages(p=>p.filter((_,i)=>i!==idx));setImagePreviews(p=>p.filter((_,i)=>i!==idx));}}
                        className="absolute top-1 right-1 bg-white border rounded-full p-1"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                {uploadingImages && <div className="text-blue-600 mt-2">Uploading images...</div>}
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button type="button" onClick={()=>{setShowCreateModal(false);resetForm();}}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Create Product</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ───────────────────────────────────────────────────── */}
      {showEditModal && editingProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b px-8 py-5 z-10 rounded-t-2xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editingProduct.name}</h3>
                  <p className="text-sm text-gray-500">SKU: {editingProduct.sku}</p>
                </div>
                <button onClick={()=>{setShowEditModal(false);setEditingProduct(null);resetForm();setEditExistingImages([]);setEditNewImages([]);setEditNewPreviews(p=>{p.forEach(URL.revokeObjectURL);return[];});}}
                  className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              {/* Tabs */}
              <div className="flex gap-1">
                {([
                  { key:'basic',    label:'📋 Basic Info' },
                  { key:'markets',  label:'🛒 Shop Listings' },
                  { key:'variants', label:'🎨 Variants' },
                ] as const).map(tab => (
                  <button key={tab.key} onClick={()=>setEditTab(tab.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      editTab === tab.key
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-8">
              {/* TAB: Basic Info */}
              {editTab === 'basic' && (
                <form onSubmit={handleUpdate} className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                        <input type="text" value={formData.name} onChange={e=>setFormData(p=>({...p,name:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Brand *</label>
                        <input type="text" value={formData.brand} onChange={e=>setFormData(p=>({...p,brand:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                        <input type="text" value={formData.model} onChange={e=>setFormData(p=>({...p,model:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                        <input type="text" value={formData.sku} onChange={e=>setFormData(p=>({...p,sku:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                        <select value={formData.category} onChange={e=>setFormData(p=>({...p,category:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required>
                          <option value="">Select category...</option>
                          {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                        </select></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select value={formData.status} onChange={e=>setFormData(p=>({...p,status:e.target.value as any}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="draft">Draft</option><option value="active">Active</option><option value="inactive">Inactive</option>
                        </select></div>
                    </div>
                    <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.description} onChange={e=>setFormData(p=>({...p,description:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" rows={3} /></div>
                    <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-2">🔗 Product URL (optional)</label>
                      <input type="url" value={formData.website} onChange={e=>setFormData(p=>({...p,website:e.target.value}))} placeholder="https://yoursite.com/product-page" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-1">Internal Reference Pricing</h4>
                    <p className="text-xs text-gray-400 mb-4">⚠️ These prices are <b>not shown to buyers</b>. Buyers see prices from Shop Listings only.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Cost / MSRP <span className="text-xs text-gray-400 font-normal">(internal only)</span></label>
                        <input type="number" step="0.01" value={formData.retailPrice} onChange={e=>setFormData(p=>({...p,retailPrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Wholesale / Buy Price <span className="text-xs text-gray-400 font-normal">(internal only)</span></label>
                        <input type="number" step="0.01" value={formData.wholesalePrice} onChange={e=>setFormData(p=>({...p,wholesalePrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                        <select value={formData.currency} onChange={e=>setFormData(p=>({...p,currency:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          {CURRENCIES.map(c=><option key={c} value={c}>{c} — {CURRENCY_LABELS[c]}</option>)}
                        </select></div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={formData.onSale} onChange={e=>setFormData(p=>({...p,onSale:e.target.checked}))} className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-600">On Sale</span>
                      </label>
                      {formData.onSale && (
                        <div className="mt-3 grid grid-cols-2 gap-6">
                          <div><label className="block text-sm font-medium text-gray-700 mb-2">Sale Price</label>
                            <input type="number" step="0.01" value={formData.salePrice} onChange={e=>setFormData(p=>({...p,salePrice:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-red-300 rounded-lg bg-red-50" /></div>
                          {formData.salePrice>0 && formData.retailPrice>0 && (
                            <div className="flex items-center">
                              <span className="text-lg font-bold text-red-600">{Math.round((1-formData.salePrice/formData.retailPrice)*100)}% OFF</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Inventory</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity</label>
                        <input type="number" value={formData.stock} onChange={e=>setFormData(p=>({...p,stock:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Low Stock Threshold</label>
                        <input type="number" value={formData.lowStockThreshold} onChange={e=>setFormData(p=>({...p,lowStockThreshold:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Reserved Stock</label>
                        <input type="number" value={formData.reserved} onChange={e=>setFormData(p=>({...p,reserved:parseInt(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">🛒 Shop Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={formData.showOnShop} onChange={e=>setFormData(p=>({...p,showOnShop:e.target.checked}))} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Sell on Shop</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={formData.freeShipping} onChange={e=>setFormData(p=>({...p,freeShipping:e.target.checked}))} className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">Free Shipping (global)</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Shipping Weight</label>
                        <input type="number" step="0.01" value={formData.shippingWeight} onChange={e=>setFormData(p=>({...p,shippingWeight:parseFloat(e.target.value)||0}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" min="0" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Weight Unit</label>
                        <select value={formData.shippingWeightUnit} onChange={e=>setFormData(p=>({...p,shippingWeightUnit:e.target.value}))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="g">Grams (g)</option><option value="kg">Kilograms (kg)</option>
                          <option value="oz">Ounces (oz)</option><option value="lb">Pounds (lb)</option>
                        </select></div>
                    </div>
                    {editingProduct?.shopStats && (editingProduct.shopStats.totalViews || editingProduct.shopStats.totalOrders) ? (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-sm font-medium text-blue-800 mb-2">Shop Performance</p>
                        <div className="flex space-x-6 text-sm text-gray-600">
                          <span>Views: <strong>{editingProduct.shopStats.totalViews||0}</strong></span>
                          <span>Orders: <strong>{editingProduct.shopStats.totalOrders||0}</strong></span>
                          <span>Revenue: <strong>{editingProduct.shopStats.totalRevenue?.toFixed(2)||'0.00'} {editingProduct.pricing?.currency}</strong></span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Product Images</h4>
                    {editExistingImages.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Images (click × to remove)</label>
                        <div className="flex flex-wrap gap-4">
                          {editExistingImages.map((img,idx)=>(
                            <div key={img.url+idx} className="relative w-24 h-24">
                              <img src={img.url} alt={img.alt||`Product ${idx+1}`} className={`w-24 h-24 object-cover rounded-lg border ${img.isPrimary?'ring-2 ring-blue-400':''}`} />
                              <button type="button" onClick={()=>handleRemoveExistingImage(idx)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"><X size={14} /></button>
                              {img.isPrimary && <span className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs text-center rounded-b-lg">Primary</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="block text-sm font-medium text-gray-700 mb-2">Add New Images</label>
                    <input type="file" multiple accept="image/*" onChange={handleEditImageSelect} className="block w-full border border-gray-300 rounded-lg p-2" />
                    {editNewPreviews.length > 0 && (
                      <div className="flex flex-wrap gap-4 mt-3">
                        {editNewPreviews.map((url,idx)=>(
                          <div key={url} className="relative w-24 h-24">
                            <img src={url} alt={`New ${idx+1}`} className="w-24 h-24 object-cover rounded-lg border border-green-300" />
                            <button type="button" onClick={()=>handleRemoveEditNewImage(idx)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"><X size={14} /></button>
                            <span className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs text-center rounded-b-lg">New</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {uploadingImages && <div className="text-blue-600 mt-2">Uploading images...</div>}
                  </div>

                  <div className="flex justify-end space-x-4 pt-4 border-t">
                    <button type="button" onClick={()=>{setShowEditModal(false);setEditingProduct(null);resetForm();setEditExistingImages([]);setEditNewImages([]);setEditNewPreviews(p=>{p.forEach(URL.revokeObjectURL);return[];});}}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button type="submit" className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700">Update Product</button>
                  </div>
                </form>
              )}

              {/* TAB: Shop Listings */}
              {editTab === 'markets' && (
                <ShopListingsTab
                  product={editingProduct}
                  businessId={getCurrentBusinessId() || ''}
                  apiBase={API_BASE}
                  token={BusinessApiService.getBusinessToken() || ''}
                  onRefresh={() => {
                    loadProducts().then(() => {
                      setProducts(prev => {
                        const updated = prev.find(p => p.productId === editingProduct?.productId);
                        if (updated) setEditingProduct({ ...updated });
                        return prev;
                      });
                    });
                  }}
                />
              )}

              {/* TAB: Variants */}
              {editTab === 'variants' && (
                <VariantsTab
                  product={editingProduct}
                  onRefresh={() => {
                    loadProducts().then(() => {
                      setProducts(prev => {
                        const updated = prev.find(p => p.productId === editingProduct?.productId);
                        if (updated) setEditingProduct({ ...updated });
                        return prev;
                      });
                    });
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── ListingForm — defined OUTSIDE ShopListingsTab so typing doesn't remount it ──
const ListingForm: React.FC<{
  initialForm:    any;
  isEditing:      boolean;
  masterVariants: any[];
  usedCountries:  string[];
  product:        Product;
  apiBase:        string;
  token:          string;
  businessId:     string;
  listingId?:     string;
  onSuccess:      () => void;
  onCancel:       () => void;
}> = ({ initialForm, isEditing, masterVariants, usedCountries, product, apiBase, token, businessId, listingId, onSuccess, onCancel }) => {

  const [form,        setForm]        = React.useState<any>(initialForm);
  const [saving,      setSaving]      = React.useState(false);
  const [uploading,   setUploading]   = React.useState(false);
  const [formTab,     setFormTab]     = React.useState<'content'|'pricing'|'shipping'|'variants'|'images'|'seo'|'limits'>('content');
  const [imgFiles,    setImgFiles]    = React.useState<File[]>([]);
  const [imgPreviews, setImgPreviews] = React.useState<string[]>([]);

  const sf    = (patch: any) => setForm((f: any) => ({ ...f, ...patch }));
  const authH = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleCountryChange = (country: string) => {
    const CUR:  Record<string,string> = { VN:'VND',JP:'JPY',AU:'AUD',GB:'GBP',CA:'CAD',SG:'SGD',KR:'KRW',TH:'THB',ID:'IDR',PH:'PHP',AE:'AED',SA:'SAR',MX:'MXN',BR:'BRL',CN:'CNY',NZ:'NZD',DE:'EUR',FR:'EUR' };
    const LANG: Record<string,string> = { VN:'vi',JP:'ja',KR:'ko',CN:'zh',TH:'th',ID:'id',FR:'fr',DE:'de' };
    const GW:   Record<string,string> = { VN:'vnpay',JP:'paypay' };
    sf({ country, currency: CUR[country]||'USD', language: LANG[country]||'en', paymentGateway: GW[country]||'stripe' });
  };

  const handleImgSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImgFiles(prev => [...prev, ...files]);
    setImgPreviews(prev => [...prev, ...files.map(URL.createObjectURL)]);
  };
  const removeNewImg      = (idx: number) => { URL.revokeObjectURL(imgPreviews[idx]); setImgFiles(p=>p.filter((_,i)=>i!==idx)); setImgPreviews(p=>p.filter((_,i)=>i!==idx)); };
  const removeExistingImg = (idx: number) => sf({ images: form.images.filter((_: any, i: number) => i !== idx) });

  const uploadImages = async (): Promise<any[]> => {
    if (!imgFiles.length) return [];
    setUploading(true);
    try {
      const fd = new FormData();
      imgFiles.forEach(f => fd.append('images', f));
      const res  = await fetch(`${apiBase}/upload/images`, { method:'POST', headers:{ 'Authorization':`Bearer ${token}` }, body:fd });
      const data = await res.json();
      return data.success ? (data.images || []) : [];
    } catch { return []; }
    finally { setUploading(false); }
  };

  const buildBody = (extraImages: any[] = []) => {
    const allImages = [...(form.images||[]), ...extraImages];
    const pid = product.productId || product._id || '';
    const variants = masterVariants.map((v: any) => ({
      name: v.name, sku: v.sku||'',
      price: form.variantPrices?.[v.name] > 0 ? form.variantPrices[v.name] : (form.price||0),
      stock: v.stock||0, isActive: v.isActive!==false, options: v.options||{},
    }));
    return {
      masterProductId: pid, businessId,
      country: form.country, language: form.language, paymentGateway: form.paymentGateway, status: form.status,
      name: form.name?.trim(), description: form.description, shortDescription: form.shortDescription,
      price: Number(form.price), currency: form.currency,
      salePrice: form.onSale && Number(form.salePrice)>0 ? Number(form.salePrice) : null,
      onSale: form.onSale, saleStart: form.saleStart||null, saleEnd: form.saleEnd||null,
      shipping: { cost: form.freeShipping?0:Number(form.shippingCost), free: form.freeShipping, shipFromCountry: form.shipFromCountry, minDays: Number(form.minDays), maxDays: Number(form.maxDays), label: form.shippingLabel },
      variants, hasVariants: variants.length>0, images: allImages,
      seo: { slug: form.seoSlug||'', metaTitle: form.seoTitle||form.name?.substring(0,60), metaDescription: form.seoDescription||form.description?.substring(0,160) },
      purchaseLimits: { minQuantity: Number(form.minQty), maxQuantity: Number(form.maxQty), maxPerCustomer: Number(form.maxPerCustomer) },
      isFeatured: form.isFeatured, showOnShop: form.showOnShop,
    };
  };

  const handleSave = async () => {
    if (!isEditing && !form.country) { alert('Select a country'); return; }
    if (!form.name?.trim())          { alert('Enter a listing name'); return; }
    if (!form.price)                 { alert('Enter a price'); return; }
    setSaving(true);
    try {
      const pid    = product.productId || product._id || '';
      const uploaded = await uploadImages();
      const url    = isEditing ? `${apiBase}/shop-listings/${listingId}` : `${apiBase}/shop-listings`;
      const method = isEditing ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers: authH, body: JSON.stringify(buildBody(uploaded)) });
      const data   = await res.json();
      if (res.ok && data.success) onSuccess();
      else alert(data.error || data.message || 'Failed to save listing');
    } catch(e: any) { alert(e.message); }
    setSaving(false);
  };

  const FORM_TABS = [
    { key:'content', label:'📝 Content' }, { key:'pricing', label:'💰 Pricing' },
    { key:'shipping', label:'📦 Shipping' }, { key:'variants', label:'🎨 Variants' },
    { key:'images', label:'🖼️ Images' }, { key:'seo', label:'🔍 SEO' },
    { key:'limits', label:'🔒 Limits' },
  ] as const;

  return (

    <div className="border border-blue-300 rounded-xl bg-white overflow-hidden shadow-sm">

      {/* Header */}
      <div className="bg-blue-600 text-white px-5 py-3 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-sm">
            {isEditing ? `Editing: ${form.country} listing` : 'Create new shop listing'}
          </h4>
          <p className="text-blue-200 text-xs mt-0.5">Master: {product.name} · {product.productId || product._id}</p>
        </div>
        <button onClick={onCancel} className="text-blue-200 hover:text-white p-1"><X size={18}/></button>
      </div>

      {/* Country / Language / Gateway (new listings only) */}
      {!isEditing && (
        <div className="px-5 pt-4 pb-4 bg-blue-50 border-b border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Country *</label>
              <select value={form.country} onChange={e => handleCountryChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Select country...</option>
                {MARKETS.filter(m => !usedCountries.includes(m.code)).map(m => (
                  <option key={m.code} value={m.code}>{m.flag} {m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Language</label>
              <select value={form.language} onChange={e => sf({ language: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Payment Gateway</label>
              <select value={form.paymentGateway} onChange={e => sf({ paymentGateway: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                {PAYMENT_GATEWAYS.map(g => <option key={g.code} value={g.code}>{g.flag} {g.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Inner tabs */}
      <div className="flex gap-0.5 px-4 pt-3 border-b overflow-x-auto bg-gray-50">
        {FORM_TABS.map(t => (
          <button key={t.key} onClick={() => setFormTab(t.key as any)}
            className={`px-3 py-2 text-xs font-medium rounded-t whitespace-nowrap transition-colors ${
              formTab === t.key
                ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-5 space-y-4 min-h-64">

        {/* ── CONTENT ────────────────────────────────────────────────────── */}
        {formTab === 'content' && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Listing Name * <span className="text-gray-400 font-normal">(in local language)</span>
              </label>
              <input type="text" value={form.name} onChange={e => sf({ name: e.target.value })}
                placeholder="Name shown to buyers in this country"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Short Description <span className="text-gray-400 font-normal">(max 300 chars)</span>
              </label>
              <input type="text" value={form.shortDescription} maxLength={300}
                onChange={e => sf({ shortDescription: e.target.value })}
                placeholder="One-line summary"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Full Description</label>
              <textarea value={form.description} onChange={e => sf({ description: e.target.value })}
                rows={5} placeholder="Full product description in local language..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y" />
            </div>
            <div className="flex flex-wrap items-center gap-5 pt-1 border-t">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.showOnShop}
                  onChange={e => sf({ showOnShop: e.target.checked })} className="w-4 h-4 text-blue-600" />
                Show on shop
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isFeatured}
                  onChange={e => sf({ isFeatured: e.target.checked })} className="w-4 h-4 text-yellow-500" />
                Featured
              </label>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">Status</label>
                <select value={form.status} onChange={e => sf({ status: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── PRICING ────────────────────────────────────────────────────── */}
        {formTab === 'pricing' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Price *</label>
                <input type="number" step="0.01" value={form.price || ''}
                  onChange={e => sf({ price: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Currency</label>
                <select value={form.currency} onChange={e => sf({ currency: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c} — {CURRENCY_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Sale Price</label>
                <input type="number" step="0.01" value={form.salePrice || ''}
                  disabled={!form.onSale}
                  onChange={e => sf({ salePrice: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.onSale} onChange={e => sf({ onSale: e.target.checked })} className="w-4 h-4 text-red-600" />
                <span className="text-red-600 font-medium">On Sale</span>
              </label>
              {form.onSale && form.salePrice > 0 && form.price > 0 && (
                <span className="bg-red-100 text-red-700 text-sm font-bold px-3 py-0.5 rounded-full">
                  {Math.round((1 - form.salePrice / form.price) * 100)}% OFF
                </span>
              )}
            </div>
            {form.onSale && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Sale Start</label>
                  <input type="datetime-local" value={form.saleStart} onChange={e => sf({ saleStart: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Sale End</label>
                  <input type="datetime-local" value={form.saleEnd} onChange={e => sf({ saleEnd: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── SHIPPING ───────────────────────────────────────────────────── */}
        {formTab === 'shipping' && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.freeShipping}
                onChange={e => sf({ freeShipping: e.target.checked, shippingCost: 0 })}
                className="w-4 h-4 text-green-600" />
              <span className="text-green-700 font-medium">Free shipping to this country</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Shipping Cost</label>
                <input type="number" step="0.01" value={form.freeShipping ? 0 : (form.shippingCost || '')}
                  disabled={form.freeShipping}
                  onChange={e => sf({ shippingCost: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Ship From</label>
                <select value={form.shipFromCountry} onChange={e => sf({ shipFromCountry: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Select...</option>
                  {MARKETS.map(m => <option key={m.code} value={m.code}>{m.flag} {m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Label <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="text" value={form.shippingLabel} onChange={e => sf({ shippingLabel: e.target.value })}
                  placeholder="e.g. Standard delivery"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Min Days</label>
                <input type="number" min={1} value={form.minDays}
                  onChange={e => sf({ minDays: parseInt(e.target.value) || 3 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Max Days</label>
                <input type="number" min={1} value={form.maxDays}
                  onChange={e => sf({ maxDays: parseInt(e.target.value) || 14 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

          </div>
        )}

        {/* ── VARIANTS ───────────────────────────────────────────────────── */}
        {formTab === 'variants' && (
          <div className="space-y-3">
            {masterVariants.length === 0 ? (
              <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-gray-500 font-medium text-sm">No variants on master product</p>
                <p className="text-xs text-gray-400 mt-1">Add variants to the master product first, then set local prices here</p>
              </div>
            ) : (
              <>
                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                  Variants are inherited from the master product. Set local prices in <b>{form.currency || 'local currency'}</b>.
                  Leave blank to use the listing base price (<b>{form.price ? `${Number(form.price).toLocaleString()} ${form.currency}` : 'not set'}</b>).
                </div>
                <div className="space-y-2">
                  {masterVariants.map((v: any) => (
                    <div key={v.name} className="flex items-center gap-4 p-3 border rounded-lg bg-white">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900">{v.name}</div>
                        {v.sku && <div className="text-xs text-gray-400">SKU: {v.sku}</div>}
                        <div className="text-xs text-gray-400">
                          Base: {v.pricing?.retail?.toLocaleString() || product.pricing?.retail?.toLocaleString()} {product.pricing?.currency}
                        </div>
                      </div>
                      <div className="w-44">
                        <label className="text-xs text-gray-600 block mb-1">Price in {form.currency || '?'}</label>
                        <input type="number" step="0.01"
                          value={form.variantPrices[v.name] || ''}
                          onChange={e => sf({ variantPrices: { ...form.variantPrices, [v.name]: parseFloat(e.target.value) || 0 } })}
                          placeholder={form.price ? String(form.price) : 'Same as base'}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── IMAGES ─────────────────────────────────────────────────────── */}
        {formTab === 'images' && (
          <div className="space-y-4">
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              Upload images for this country listing. If left empty, the master product images are used automatically.
            </div>

            {form.images.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-2">Current Images</label>
                <div className="flex flex-wrap gap-3">
                  {form.images.map((img: any, idx: number) => (
                    <div key={idx} className="relative w-24 h-24">
                      <img src={img.url} alt={img.alt || `Image ${idx+1}`}
                        className={`w-24 h-24 object-cover rounded-lg border-2 ${img.isPrimary ? 'border-blue-400' : 'border-gray-200'}`} />
                      <button type="button" onClick={() => removeExistingImg(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600">
                        <X size={10}/>
                      </button>
                      {img.isPrimary && <span className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs text-center rounded-b-lg py-0.5">Primary</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">
                Add New Images <span className="text-gray-400 font-normal">(jpg, png, webp)</span>
              </label>
              <input type="file" multiple accept="image/*" onChange={handleImgSelect}
                className="block w-full border border-gray-300 rounded-lg p-2 text-sm" />
              {imgPreviews.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {imgPreviews.map((url, idx) => (
                    <div key={url} className="relative w-24 h-24">
                      <img src={url} alt={`New ${idx+1}`} className="w-24 h-24 object-cover rounded-lg border-2 border-green-300" />
                      <button type="button" onClick={() => removeNewImg(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600">
                        <X size={10}/>
                      </button>
                      <span className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs text-center rounded-b-lg py-0.5">New</span>
                    </div>
                  ))}
                </div>
              )}
              {uploading && <p className="text-blue-600 text-xs mt-2 flex items-center gap-1"><RefreshCw size={12} className="animate-spin"/> Uploading...</p>}
            </div>
          </div>
        )}

        {/* ── SEO ────────────────────────────────────────────────────────── */}
        {formTab === 'seo' && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                URL Slug <span className="text-gray-400 font-normal">(auto-generated if empty)</span>
              </label>
              <input type="text" value={form.seoSlug}
                onChange={e => sf({ seoSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder={`${(form.name || product.name).toLowerCase().replace(/[^a-z0-9]+/g,'-')}-${(form.country||'').toLowerCase()}`}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Meta Title <span className="text-gray-400 font-normal">(max 60 chars)</span>
              </label>
              <input type="text" value={form.seoTitle} maxLength={60}
                onChange={e => sf({ seoTitle: e.target.value })}
                placeholder={form.name.substring(0, 60)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="text-right text-xs text-gray-400 mt-1">{form.seoTitle.length}/60</div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Meta Description <span className="text-gray-400 font-normal">(max 160 chars)</span>
              </label>
              <textarea value={form.seoDescription} maxLength={160}
                onChange={e => sf({ seoDescription: e.target.value })}
                rows={3} placeholder={form.description?.substring(0, 160)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              <div className="text-right text-xs text-gray-400 mt-1">{form.seoDescription.length}/160</div>
            </div>
          </div>
        )}

        {/* ── LIMITS ─────────────────────────────────────────────────────── */}
        {formTab === 'limits' && (
          <div className="space-y-4">
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              Control how many items a buyer can purchase.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Min Qty per Order</label>
                <input type="number" min={1} value={form.minQty}
                  onChange={e => sf({ minQty: parseInt(e.target.value) || 1 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Max Qty per Order</label>
                <input type="number" min={1} value={form.maxQty}
                  onChange={e => sf({ maxQty: parseInt(e.target.value) || 100 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Max per Customer</label>
                <input type="number" min={1} value={form.maxPerCustomer}
                  onChange={e => sf({ maxPerCustomer: parseInt(e.target.value) || 10 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {isEditing
            ? `Editing ${form.country} · ${form.currency}`
            : form.country
              ? `${MARKETS.find(m => m.code === form.country)?.flag} ${form.country} · ${form.currency} · ${PAYMENT_GATEWAYS.find(g => g.code === form.paymentGateway)?.label}`
              : 'Select a country to continue'}
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || uploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2">
            {saving || uploading
              ? <><RefreshCw size={13} className="animate-spin"/>{uploading ? 'Uploading...' : 'Saving...'}</>
              : isEditing ? 'Update Listing' : 'Create Listing'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ShopListingsTab: React.FC<{
  product: Product;
  businessId: string;
  apiBase: string;
  token: string;
  onRefresh: () => void;
}> = ({ product, businessId, apiBase, token, onRefresh }) => {
  const pid = product.productId || product._id || '';

  const [listings,    setListings]    = React.useState<ShopListing[]>([]);
  const [loading,     setLoading]     = React.useState(true);
  const [showForm,    setShowForm]    = React.useState(false);
  const [editingId,   setEditingId]   = React.useState<string|null>(null);
  const [saving,      setSaving]      = React.useState(false);
  const [currentForm, setCurrentForm] = React.useState<any>(null);
  const [formTab,     setFormTab]     = React.useState<'content'|'pricing'|'shipping'|'variants'|'images'|'seo'|'limits'>('content');
  const [imgFiles,    setImgFiles]    = React.useState<File[]>([]);
  const [imgPreviews, setImgPreviews] = React.useState<string[]>([]);
  const [uploading,   setUploading]   = React.useState(false);

  const authH = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Blank form ───────────────────────────────────────────────────────────────
  const blankForm = (): any => ({
    country: '', language: 'en', paymentGateway: 'stripe', status: 'draft',
    name: product.name || '', description: product.description || '', shortDescription: '',
    price: 0, currency: 'USD', salePrice: 0, onSale: false, saleStart: '', saleEnd: '',
    shippingCost: 0, freeShipping: false, shipFromCountry: '',
    minDays: 3, maxDays: 14, shippingLabel: '',
    variantPrices: {} as Record<string, number>,
    images: [] as any[],
    seoSlug: '', seoTitle: '', seoDescription: '',
    minQty: 1, maxQty: 100, maxPerCustomer: 10,
    isFeatured: false, showOnShop: true,
  });

  const [form, setForm] = React.useState<any>(blankForm());
  const sf = (patch: any) => setForm((f: any) => ({ ...f, ...patch }));

  // ── Load listings ────────────────────────────────────────────────────────────
  const reload = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${apiBase}/shop-listings?masterProductId=${pid}`, { headers: authH });
      const data = await res.json();
      if (data.success) setListings(data.data?.listings || []);
    } catch {}
    setLoading(false);
  };

  React.useEffect(() => { reload(); }, [pid]);

  const usedCountries = listings.filter(l => l.listingId !== editingId).map(l => l.country);

  // ── Auto-fill country defaults ────────────────────────────────────────────────
  const handleCountryChange = (country: string) => {
    const CUR: Record<string,string>  = { VN:'VND',JP:'JPY',AU:'AUD',GB:'GBP',CA:'CAD',SG:'SGD',KR:'KRW',TH:'THB',ID:'IDR',PH:'PHP',AE:'AED',SA:'SAR',MX:'MXN',BR:'BRL',CN:'CNY',NZ:'NZD',DE:'EUR',FR:'EUR' };
    const LANG: Record<string,string> = { VN:'vi',JP:'ja',KR:'ko',CN:'zh',TH:'th',ID:'id',FR:'fr',DE:'de' };
    const GW: Record<string,string>   = { VN:'vnpay',JP:'paypay' };
    sf({ country, currency: CUR[country]||'USD', language: LANG[country]||'en', paymentGateway: GW[country]||'stripe' });
  };

  // ── Image handlers ────────────────────────────────────────────────────────────
  const handleImgSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImgFiles(prev => [...prev, ...files]);
    setImgPreviews(prev => [...prev, ...files.map(URL.createObjectURL)]);
  };
  const removeNewImg = (idx: number) => {
    URL.revokeObjectURL(imgPreviews[idx]);
    setImgFiles(prev => prev.filter((_,i) => i !== idx));
    setImgPreviews(prev => prev.filter((_,i) => i !== idx));
  };
  const removeExistingImg = (idx: number) => sf({ images: form.images.filter((_: any, i: number) => i !== idx) });

  const uploadImages = async (): Promise<any[]> => {
    if (!imgFiles.length) return [];
    setUploading(true);
    try {
      const fd = new FormData();
      imgFiles.forEach(f => fd.append('images', f));
      const res  = await fetch(`${apiBase}/upload/images`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      return data.success ? (data.images || []) : [];
    } catch { return []; }
    finally { setUploading(false); }
  };

  // ── Build request body ────────────────────────────────────────────────────────
  const buildBody = (extraImages: any[] = []) => {
    const allImages = [...form.images, ...extraImages];
    const masterVariants = (product as any).variants || [];
    const variants = masterVariants.map((v: any) => ({
      name:     v.name,
      sku:      v.sku || '',
      price:    form.variantPrices[v.name] > 0 ? form.variantPrices[v.name] : (form.price || 0),
      stock:    v.stock || 0,
      isActive: v.isActive !== false,
      options:  v.options || {},
    }));
    return {
      masterProductId: pid,
      businessId,
      country:         form.country,
      language:        form.language,
      paymentGateway:  form.paymentGateway,
      status:          form.status,
      name:            form.name.trim(),
      description:     form.description,
      shortDescription:form.shortDescription,
      price:           Number(form.price),
      currency:        form.currency,
      salePrice:       form.onSale && Number(form.salePrice) > 0 ? Number(form.salePrice) : null,
      onSale:          form.onSale,
      saleStart:       form.saleStart || null,
      saleEnd:         form.saleEnd   || null,
      shipping: {
        cost:            form.freeShipping ? 0 : Number(form.shippingCost),
        free:            form.freeShipping,
        shipFromCountry: form.shipFromCountry,
        minDays:         Number(form.minDays),
        maxDays:         Number(form.maxDays),
        label:           form.shippingLabel,
      },
      variants,
      hasVariants: variants.length > 0,
      images:      allImages,
      seo: {
        slug:            form.seoSlug || '',
        metaTitle:       form.seoTitle       || form.name.substring(0, 60),
        metaDescription: form.seoDescription || form.description?.substring(0, 160),
      },
      purchaseLimits: {
        minQuantity:    Number(form.minQty),
        maxQuantity:    Number(form.maxQty),
        maxPerCustomer: Number(form.maxPerCustomer),
      },
      isFeatured: form.isFeatured,
      showOnShop: form.showOnShop,
    };
  };

  // ── Create / Update / Delete ──────────────────────────────────────────────────
  const cancelForm = () => {
    setShowForm(false); setEditingId(null);
    setForm(blankForm()); setImgFiles([]); setImgPreviews([]);
    setFormTab('content');
  };

  const handleAdd = async () => {
    if (!form.country)     { alert('Select a country'); return; }
    if (!form.name.trim()) { alert('Enter a listing name'); return; }
    if (!form.price)       { alert('Enter a price'); return; }
    setSaving(true);
    try {
      const uploaded = await uploadImages();
      const res  = await fetch(`${apiBase}/shop-listings`, {
        method: 'POST', headers: authH, body: JSON.stringify(buildBody(uploaded))
      });
      const data = await res.json();
      if (res.ok && data.success) {
        cancelForm(); await reload(); onRefresh();
      } else alert(data.error || data.message || 'Failed to create listing');
    } catch(e: any) { alert(e.message); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) { alert('Enter a listing name'); return; }
    if (!form.price)       { alert('Enter a price'); return; }
    setSaving(true);
    try {
      const uploaded = await uploadImages();
      const res  = await fetch(`${apiBase}/shop-listings/${editingId}`, {
        method: 'PUT', headers: authH, body: JSON.stringify(buildBody(uploaded))
      });
      const data = await res.json();
      if (res.ok && data.success) {
        cancelForm(); await reload(); onRefresh();
      } else alert(data.error || 'Failed to update listing');
    } catch(e: any) { alert(e.message); }
    setSaving(false);
  };

  const handleDelete = async (listingId: string, country: string) => {
    if (!window.confirm(`Delete ${country} listing? Removes it from the shop.`)) return;
    try {
      const res  = await fetch(`${apiBase}/shop-listings/${listingId}`, { method: 'DELETE', headers: authH });
      const data = await res.json();
      if (res.ok && data.success) { await reload(); onRefresh(); }
      else alert(data.error || 'Failed to delete');
    } catch(e: any) { alert(e.message); }
  };

  const handleToggle = async (l: ShopListing) => {
    const newActive = !l.isActive;
    try {
      await fetch(`${apiBase}/shop-listings/${l.listingId}`, {
        method: 'PUT', headers: authH,
        body: JSON.stringify({ isActive: newActive, status: newActive ? 'active' : 'inactive' }),
      });
      await reload();
    } catch {}
  };

  const startEdit = (l: ShopListing) => {
    setEditingId(l.listingId);
    setShowForm(false);
    const vp: Record<string,number> = {};
    (l as any).variants?.forEach((v: any) => { vp[v.name] = v.price; });
    const editForm = {
      country: l.country, language: l.language, paymentGateway: l.paymentGateway, status: l.status,
      name: l.name, description: l.description || '', shortDescription: (l as any).shortDescription || '',
      price: l.price, currency: l.currency,
      salePrice: l.salePrice || 0, onSale: l.onSale || false, saleStart: '', saleEnd: '',
      shippingCost: l.shipping?.cost || 0, freeShipping: l.shipping?.free || false,
      shipFromCountry: l.shipping?.shipFromCountry || '',
      minDays: l.shipping?.minDays || 3, maxDays: l.shipping?.maxDays || 14,
      shippingLabel: l.shipping?.label || '',
      variantPrices: vp,
      images: (l as any).images || [],
      seoSlug:        (l as any).seo?.slug            || '',
      seoTitle:       (l as any).seo?.metaTitle        || '',
      seoDescription: (l as any).seo?.metaDescription  || '',
      minQty:         (l as any).purchaseLimits?.minQuantity    || 1,
      maxQty:         (l as any).purchaseLimits?.maxQuantity    || 100,
      maxPerCustomer: (l as any).purchaseLimits?.maxPerCustomer || 10,
      isFeatured: l.isFeatured || false,
      showOnShop: (l as any).showOnShop !== false,
    };
    setCurrentForm(editForm);
    setForm(editForm);
  };

  const isEditing  = !!editingId;
  const masterVariants = (product as any).variants || [];

  const FORM_TABS = [
    { key:'content',  label:'📝 Content'  },
    { key:'pricing',  label:'💰 Pricing'  },
    { key:'shipping', label:'📦 Shipping' },
    { key:'variants', label:'🎨 Variants' },
    { key:'images',   label:'🖼️ Images'   },
    { key:'seo',      label:'🔍 SEO'      },
    { key:'limits',   label:'🔒 Limits'   },
  ] as const;

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <b>Shop Listings</b> — One listing per country. Each has its own language, price, currency, payment gateway, images and SEO.
        Tags and batches always link to the master product above.
      </div>

      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border text-sm">
        <Package size={15} className="text-gray-400 flex-shrink-0"/>
        <span className="text-gray-500">Master:</span>
        <span className="font-semibold text-gray-900 truncate">{product.name}</span>
        <span className="font-mono text-xs text-gray-400 truncate">{pid}</span>
        <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">Tags + batches → here</span>
      </div>

      {/* Form */}
      {(showForm || editingId) && (
        <ListingForm
          key={editingId || 'new'}
          initialForm={currentForm || blankForm()}
          isEditing={!!editingId}
          masterVariants={(product as any).variants || []}
          usedCountries={listings.filter(l => l.listingId !== editingId).map(l => l.country)}
          product={product}
          apiBase={apiBase}
          token={token}
          businessId={businessId}
          listingId={editingId || undefined}
          onSuccess={async () => { setShowForm(false); setEditingId(null); setCurrentForm(null); await reload(); onRefresh(); }}
          onCancel={() => { setShowForm(false); setEditingId(null); setCurrentForm(null); setForm(blankForm()); }}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm flex items-center justify-center gap-2">
          <RefreshCw size={14} className="animate-spin"/> Loading listings...
        </div>
      ) : listings.length === 0 && !showForm ? (
        <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-xl">
          <Globe size={36} className="mx-auto text-gray-300 mb-3"/>
          <p className="text-gray-500 font-medium">No shop listings yet</p>
          <p className="text-gray-400 text-xs mt-1 mb-5">Create a listing for each country you sell in</p>
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(blankForm()); }}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm hover:bg-blue-700 inline-flex items-center gap-2 font-medium">
            <Plus size={14}/> Create First Listing
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {listings.map(l => (
            <div key={l.listingId}
              className={`border rounded-xl p-4 bg-white transition-all ${
                editingId === l.listingId ? 'border-blue-300 bg-blue-50/20'
                : l.isActive && l.status === 'active' ? 'border-green-200'
                : 'border-gray-200'
              }`}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className="text-2xl mt-0.5">{MARKETS.find(m => m.code === l.country)?.flag || '🌐'}</span>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900">{l.country}</span>
                    <span className="font-bold text-blue-700 text-base">{Number(l.price).toLocaleString()} {l.currency}</span>
                    {l.onSale && l.salePrice && (
                      <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                        SALE {Number(l.salePrice).toLocaleString()} {l.currency}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
                    <span>{LANGUAGES.find(lang => lang.code === l.language)?.label || l.language}</span>
                    <span>·</span>
                    <span>{PAYMENT_GATEWAYS.find(g => g.code === l.paymentGateway)?.flag} {l.paymentGateway}</span>
                    {l.shipping?.free && <><span>·</span><span className="text-green-600">Free shipping</span></>}
                    {!l.shipping?.free && l.shipping?.cost ? <><span>·</span><span>+{l.shipping.cost} ship</span></> : null}
                    {l.shipping?.minDays && l.shipping?.maxDays && <><span>·</span><span>{l.shipping.minDays}-{l.shipping.maxDays}d</span></>}
                  </div>
                  <p className="text-xs text-gray-400 truncate max-w-md">{l.name}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap ml-auto">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    l.status === 'active' && l.isActive ? 'bg-green-100 text-green-700'
                    : l.status === 'draft' ? 'bg-gray-100 text-gray-600'
                    : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {l.status === 'active' && l.isActive ? '● Live' : l.status}
                  </span>
                  <button onClick={() => handleToggle(l)}
                    className={`text-xs px-2 py-1 rounded border ${l.isActive ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}>
                    {l.isActive ? 'Pause' : 'Activate'}
                  </button>
                  <button onClick={() => startEdit(l)}
                    className="text-blue-600 text-xs px-2 py-1 border border-blue-200 rounded hover:bg-blue-50">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(l.listingId, l.country)}
                    className="text-red-500 text-xs px-2 py-1 border border-red-200 rounded hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add button */}
      {!showForm && !editingId && listings.length > 0 && (
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm(blankForm()); }}
          className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium">
          <Plus size={14}/> Add Country Listing
        </button>
      )}

    </div>
  );
};


export default ProductsPage;